// src/utils/api.js
import axios from 'axios';
import { store } from '../store';
import { logout, refreshToken, setTokens } from '../store/actions/authActions';

// Security configuration
const SECURITY_CONFIG = {
  REFRESH_TOKEN_ENDPOINT: '/auth/refresh',
  TOKEN_ROTATION: true,
  MAX_REFRESH_ATTEMPTS: 3,
  REFRESH_WINDOW: 5 * 60 * 1000, // 5 minutes before expiry to refresh proactively
};

// Detect client type
const getClientType = () => {
  // Check if running in React Native or similar mobile environment
  if (typeof navigator !== 'undefined' && navigator.product === 'ReactNative') {
    return 'mobile';
  }
  // Check if running in web browser
  if (typeof window !== 'undefined') {
    return 'web';
  }
  return 'unknown';
};

const CLIENT_TYPE = getClientType();

// Create axios instance
const api = axios.create({
  baseURL: process.env.REACT_APP_API_URL || 'http://localhost:4000/api/v1/stag',
  withCredentials: CLIENT_TYPE === 'web', // Only send cookies for web clients
  timeout: 30000, // 30 seconds timeout
  headers: {
    'Content-Type': 'application/json',
    'X-Client-Type': CLIENT_TYPE,
    'X-Client-Version': process.env.REACT_APP_VERSION || '1.0.0',
  },
});

// Token management state
let isRefreshing = false;
let failedQueue = [];
let refreshAttempts = 0;

const processQueue = (error, token = null) => {
  failedQueue.forEach(prom => {
    if (error) {
      prom.reject(error);
    } else {
      prom.resolve(token);
    }
  });
  
  failedQueue = [];
  refreshAttempts = 0; // Reset attempts on successful refresh
};

// Secure token storage with platform-specific handling
const tokenStorage = {
  accessToken: null,
  
  // Set access token
  setAccessToken(token) {
    this.accessToken = token;
    
    // Store based on client type
    if (CLIENT_TYPE === 'web') {
      // For web, use localStorage with optional encryption
      try {
        if (process.env.NODE_ENV === 'production') {
          // In production, consider using sessionStorage for better security
          sessionStorage.setItem('access_token', token);
        } else {
          localStorage.setItem('token', token);
        }
      } catch (error) {
        console.warn('Failed to store token:', error);
      }
    }
    // For mobile, tokens are stored by the mobile app using AsyncStorage
    // This is handled separately in the mobile app code
  },
  
  // Get access token
  getAccessToken() {
    // Return from memory if available
    if (this.accessToken) return this.accessToken;
    
    // Try to get from storage based on client type
    if (CLIENT_TYPE === 'web') {
      try {
        if (process.env.NODE_ENV === 'production') {
          return sessionStorage.getItem('access_token');
        }
        return localStorage.getItem('token');
      } catch (error) {
        console.warn('Failed to retrieve token:', error);
        return null;
      }
    }
    
    return null;
  },
  
  // Get refresh token (for mobile clients)
  getRefreshToken() {
    if (CLIENT_TYPE === 'mobile') {
      // For mobile, refresh token is stored by the mobile app
      // This method will be overridden by mobile app code
      return null;
    }
    return null; // Web uses HTTP-only cookies
  },
  
  // Clear all tokens
  clearTokens() {
    this.accessToken = null;
    
    if (CLIENT_TYPE === 'web') {
      try {
        localStorage.removeItem('token');
        sessionStorage.removeItem('access_token');
        localStorage.removeItem('refresh_token');
      } catch (error) {
        console.warn('Failed to clear tokens:', error);
      }
    }
  }
};

// Check if token needs proactive refresh
const shouldRefreshToken = (token) => {
  if (!token) return false;
  
  try {
    const payload = JSON.parse(atob(token.split('.')[1]));
    const expiryTime = payload.exp * 1000; // Convert to milliseconds
    const currentTime = Date.now();
    
    // Refresh if token expires within the refresh window
    return (expiryTime - currentTime) < SECURITY_CONFIG.REFRESH_WINDOW;
  } catch (error) {
    console.warn('Failed to parse token for refresh check:', error);
    return false;
  }
};

// Function to refresh access token (handles both web and mobile)
const refreshAccessToken = async () => {
  if (refreshAttempts >= SECURITY_CONFIG.MAX_REFRESH_ATTEMPTS) {
    store.dispatch(logout());
    tokenStorage.clearTokens();
    throw new Error('Max refresh attempts exceeded');
  }

  refreshAttempts++;
  
  try {
    let response;
    
    if (CLIENT_TYPE === 'web') {
      // Web: use HTTP-only cookie (automatically sent)
      response = await axios.post(
        `${api.defaults.baseURL}${SECURITY_CONFIG.REFRESH_TOKEN_ENDPOINT}`,
        {}, // Empty body as refresh token is in cookie
        {
          withCredentials: true,
          headers: {
            'X-Refresh-Attempt': refreshAttempts,
            'X-Client-Type': 'web',
          }
        }
      );
    } else {
      // Mobile: send refresh token in request body
      const refreshToken = tokenStorage.getRefreshToken();
      if (!refreshToken) {
        throw new Error('No refresh token available');
      }
      
      response = await axios.post(
        `${api.defaults.baseURL}${SECURITY_CONFIG.REFRESH_TOKEN_ENDPOINT}`,
        { refreshToken },
        {
          headers: {
            'X-Refresh-Attempt': refreshAttempts,
            'X-Client-Type': 'mobile',
          }
        }
      );
    }

    const { accessToken, refreshToken: newRefreshToken } = response.data;
    
    // Store new access token
    tokenStorage.setAccessToken(accessToken);
    
    // For mobile, store new refresh token if provided
    if (CLIENT_TYPE === 'mobile' && newRefreshToken) {
      // Mobile apps should handle this through their storage mechanism
      // This will be overridden by mobile-specific code
      localStorage.setItem('refresh_token', newRefreshToken);
    }
    
    // Update Redux store
    store.dispatch(refreshToken(accessToken));
    
    return accessToken;
  } catch (error) {
    console.error('Token refresh failed:', error);
    
    // If refresh fails with 401, it means the refresh token is invalid
    if (error.response?.status === 401) {
      store.dispatch(logout());
      tokenStorage.clearTokens();
    }
    throw error;
  }
};

// Request interceptor
api.interceptors.request.use(
  async (config) => {
    // Skip for public endpoints
    if (config.public) {
      return config;
    }

    // Skip token handling for refresh endpoint
    if (config.url.includes(SECURITY_CONFIG.REFRESH_TOKEN_ENDPOINT)) {
      return config;
    }

    const token = tokenStorage.getAccessToken();
    
    if (token) {
      // Check if token needs proactive refresh
      if (shouldRefreshToken(token)) {
        try {
          const newToken = await refreshAccessToken();
          config.headers.Authorization = `Bearer ${newToken}`;
        } catch (error) {
          // If refresh fails, proceed with current token
          config.headers.Authorization = `Bearer ${token}`;
        }
      } else {
        config.headers.Authorization = `Bearer ${token}`;
      }
    }
    
    // Add security headers
    config.headers['X-Requested-With'] = 'XMLHttpRequest';
    config.headers['X-Content-Type-Options'] = 'nosniff';
    
    // Add cache control for sensitive endpoints
    if (config.method === 'get' && config.url.includes('/auth/')) {
      config.headers['Cache-Control'] = 'no-cache, no-store, must-revalidate';
      config.headers['Pragma'] = 'no-cache';
      config.headers['Expires'] = '0';
    }
    
    return config;
  },
  (error) => {
    return Promise.reject(error);
  }
);

// Response interceptor
api.interceptors.response.use(
  (response) => {
    // Check for new tokens in response headers (alternative method)
    const newAccessToken = response.headers['x-new-access-token'];
    if (newAccessToken) {
      tokenStorage.setAccessToken(newAccessToken);
      store.dispatch(refreshToken(newAccessToken));
    }
    
    return response;
  },
  async (error) => {
    const originalRequest = error.config;

    // Don't retry if it's a public endpoint
    if (originalRequest?.public) {
      return Promise.reject(error);
    }

    // Handle network errors
    if (!error.response) {
      console.error('Network error:', error);
      return Promise.reject({
        ...error,
        message: 'Network error. Please check your connection.',
        isNetworkError: true
      });
    }

    // Handle 401 errors (token expired)
    if (error.response?.status === 401 && !originalRequest?._retry) {
      
      // Don't attempt refresh on the refresh endpoint itself
      if (originalRequest?.url?.includes(SECURITY_CONFIG.REFRESH_TOKEN_ENDPOINT)) {
        store.dispatch(logout());
        tokenStorage.clearTokens();
        return Promise.reject(error);
      }

      // Don't attempt refresh if no original request
      if (!originalRequest) {
        store.dispatch(logout());
        tokenStorage.clearTokens();
        return Promise.reject(error);
      }

      if (isRefreshing) {
        // Queue the request while token is being refreshed
        return new Promise((resolve, reject) => {
          failedQueue.push({ resolve, reject });
        })
          .then(token => {
            originalRequest.headers.Authorization = `Bearer ${token}`;
            return api(originalRequest);
          })
          .catch(err => {
            return Promise.reject(err);
          });
      }

      originalRequest._retry = true;
      isRefreshing = true;

      try {
        const newToken = await refreshAccessToken();
        
        // Update the original request with new token
        originalRequest.headers.Authorization = `Bearer ${newToken}`;
        
        // Process queued requests
        processQueue(null, newToken);
        
        return api(originalRequest);
      } catch (refreshError) {
        processQueue(refreshError, null);
        store.dispatch(logout());
        tokenStorage.clearTokens();
        return Promise.reject(refreshError);
      } finally {
        isRefreshing = false;
      }
    }

    // Handle 403 errors (insufficient permissions)
    if (error.response?.status === 403) {
      console.error('Access forbidden:', error.response.data);
      error.message = 'You do not have permission to perform this action';
    }

    // Handle 429 errors (rate limiting)
    if (error.response?.status === 429) {
      error.message = 'Too many requests. Please try again later.';
    }

    // Handle 500 errors (server errors)
    if (error.response?.status >= 500) {
      error.message = 'Server error. Please try again later.';
    }

    return Promise.reject(error);
  }
);

// Helper method for public endpoints (no auth required)
api.public = (config) => {
  return api({
    ...config,
    public: true,
  });
};

// Method to clear all tokens (for logout)
api.clearTokens = () => {
  tokenStorage.clearTokens();
  isRefreshing = false;
  failedQueue = [];
  refreshAttempts = 0;
};

// Method to set tokens (for mobile apps)
api.setTokens = (accessToken, refreshToken) => {
  if (accessToken) {
    tokenStorage.setAccessToken(accessToken);
    store.dispatch(setTokens(accessToken, refreshToken));
  }
};

// Method to get current auth state
api.getAuthState = () => {
  return {
    isAuthenticated: !!tokenStorage.getAccessToken(),
    clientType: CLIENT_TYPE,
    hasToken: !!tokenStorage.getAccessToken(),
  };
};

// Initialize token from storage
const initializeToken = () => {
  if (CLIENT_TYPE === 'web') {
    try {
      let token = null;
      if (process.env.NODE_ENV === 'production') {
        token = sessionStorage.getItem('access_token');
      } else {
        token = localStorage.getItem('token');
      }
      
      if (token) {
        tokenStorage.setAccessToken(token);
        
        // Verify token is still valid
        try {
          const payload = JSON.parse(atob(token.split('.')[1]));
          const expiryTime = payload.exp * 1000;
          
          if (Date.now() >= expiryTime) {
            // Token expired, clear it
            tokenStorage.clearTokens();
          }
        } catch (error) {
          // Invalid token, clear it
          tokenStorage.clearTokens();
        }
      }
    } catch (error) {
      console.warn('Failed to initialize token:', error);
    }
  }
};

// Run initialization
initializeToken();

// Export for mobile apps to override storage methods
api.__overrideTokenStorage = (customStorage) => {
  Object.assign(tokenStorage, customStorage);
};

export default api;